import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "../../atoms/inputs/TextField";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { theme } from "../../../theme";
import { Col } from "react-grid-system";
import BodySmall from "../../atoms/typography/BodySmall";
import { CopyIcon } from "../../atoms/icons/CopyIcon";
import Modal from "../../molecules/Modal";
import ManageGroupModalContent from "./ManageGroupModalContent";
import DeleteModal from "../../molecules/modals/DeleteModal";
import Card from "../../atoms/Card";
import { useUserGroups } from "../../../graphql/hooks/rolesAndPermissions/useUserGroups";
import Small from "../../atoms/typography/Small";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import TextTag from "../../atoms/TextTag";
import Alert from "../../molecules/Alert";
import Toast from "../../molecules/Toast";
import { useDeleteGroup } from "../../../graphql/hooks/rolesAndPermissions/useDeleteGroup";
import { useTranslation } from "react-i18next";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText, {
  TableHeaderTextSorting,
} from "../../atoms/typography/TableHeaderText";
import TableRow from "../../atoms/TableRow";
import IconButton from "../../atoms/buttons/IconButton";
import PaginationComponent from "../../molecules/PaginationComponent";
import Tooltip from "../../molecules/Tooltip";
import type { SortDirection } from "../../../models/SortDirection";
import { CopyPersonIcon } from "../../atoms/icons/CopyPersonIcon";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { GroupsRoute } from "../../../utils/routes";

const ITEMS_PER_PAGE = 20;

const GroupsList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [searchGroupString, setSearchGroupString] = useState("");
  const [sort, setSort] = useState<SortDirection>("ASC");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [deleteGroupSuccessful, setDeleteGroupSuccessful] = useState(false);

  const { userGroups, error, loading } = useUserGroups(
    searchGroupString,
    sort,
    currentPageIndex * ITEMS_PER_PAGE,
    ITEMS_PER_PAGE,
  );

  const [copyGroupIndex, setCopyGroupIndex] = useState<number | undefined>(
    undefined,
  );
  const [copyGroupWithUsers, setCopyGroupWithUsers] = useState(false);

  const onCompletedDeleteGroup = (res?: any) => {
    if (res?.rolesAndPermissions?.deleteGroup?.success) {
      setErrorMessage(undefined);
      setDeleteGroupSuccessful(true);
      setTimeout(() => {
        setDeleteGroupSuccessful(false);
      }, 3000);
    } else {
      setErrorMessage(t("errors.groupCouldNotBeDeleted"));
    }
  };
  const onErrorDeleteGroup = (e?: any) => {
    setErrorMessage(e?.message || t("errors.groupCouldNotBeDeleted"));
  };
  const { deleteGroup } = useDeleteGroup({
    onCompleted: onCompletedDeleteGroup,
    onError: onErrorDeleteGroup,
  });
  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroup({
        variables: {
          input: {
            groupId: groupId,
          },
        },
      });
    } catch (error) {
      setErrorMessage(error?.toString() || t("errors.groupCouldNotBeDeleted"));
    }
  };

  return (
    <div>
      <TextField
        placeholder={t("users.searchGroup")}
        value={searchGroupString}
        onChange={setSearchGroupString}
      />
      <HorizontalSpacer spacing={2} />
      <Card spacing={0}>
        <TableHeader>
          <Col lg={1.5}>
            <TableHeaderTextSorting
              onClickSorting={() => {
                sort === "ASC" ? setSort("DESC") : setSort("ASC");
              }}
            >
              {t("users.group")}
            </TableHeaderTextSorting>
          </Col>
          <Col lg={4.5}>
            <TableHeaderText>Beskrivning</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>Medlemmar</TableHeaderText>
          </Col>
          <Col lg={2.6}>
            <TableHeaderText>Admins</TableHeaderText>
          </Col>
        </TableHeader>

        {loading && !userGroups && <LoadingSpinner />}
        {error && (
          <Alert
            type="error"
            title={t("errors.couldNotFetchGroups")}
            message={error.message}
          />
        )}
        {errorMessage && (
          <Alert
            type="error"
            title={t("errors.groupCouldNotBeDeleted")}
            message={errorMessage}
          />
        )}

        {userGroups?.items?.map((group, index) => (
          <TableRow
            key={group.id}
            align="center"
            onClick={() => {
              navigate(`/groups/${group.id}`, {
                state: { group: group, from: GroupsRoute.id },
              });
            }}
          >
            <Col
              lg={1.5}
              style={{ display: "flex", gap: 8, alignItems: "center" }}
            >
              <BodySmall fontweight={500}>{group.name}</BodySmall>
              {group.isSystemGroup && (
                <TextTag tagTitleKey={t("users.systemGroup")} />
              )}
            </Col>
            <Col lg={4.5}>
              <Small fontweight={400}>{group.description}</Small>
            </Col>
            <Col lg={1.5}>
              <BodySmall>
                {group?.memberCount} {t("users.members")}
              </BodySmall>
            </Col>
            <Col lg={2.6}>
              <BodySmall>
                {group?.members
                  ?.filter((member) => member?.isGroupAdmin)
                  .map(
                    (member) =>
                      member?.skynetUser?.firstName +
                      " " +
                      member?.skynetUser?.lastName,
                  )
                  .join(", ")}
              </BodySmall>
            </Col>
            <Col
              lg={1.9}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {/*
              <IconButton
                icon={<ViewOnIcon color={theme.colors.primary} />}
                onClick={() => {
                  setSelectedGroupIndex(index);
                  setManageGroupOpen(true);
                }}
                size="medium"
                color={theme.colors.primary20}
              />*/}
              <Tooltip text={t("users.duplicateWithUsers")}>
                <IconButton
                  icon={<CopyPersonIcon color={theme.colors.primary} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCopyGroupIndex(index);
                    setCopyGroupWithUsers(true);
                  }}
                  size="medium"
                />
              </Tooltip>
              <Tooltip text={t("users.duplicateWithoutUsers")}>
                <IconButton
                  icon={<CopyIcon color={theme.colors.primary} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCopyGroupIndex(index);
                    setCopyGroupWithUsers(false);
                  }}
                  size="medium"
                />
              </Tooltip>
              <div onClick={(e) => e.stopPropagation()}>
                <DeleteModal
                  deleteButtonType="iconButtonMedium"
                  onClickDelete={() => handleDeleteGroup(group.id)}
                  disabled={group.isSystemGroup}
                />
              </div>
              <ChevronSmallRightIcon />
            </Col>
          </TableRow>
        ))}
        <PaginationComponent
          currentPageIndex={currentPageIndex}
          numberOfPages={Math.ceil(
            (userGroups?.totalCount ?? 0) / ITEMS_PER_PAGE,
          )}
          setCurrentPageIndex={setCurrentPageIndex}
          spacing={2}
        />
      </Card>
      <HorizontalSpacer spacing={1} />

      {copyGroupIndex !== undefined &&
        userGroups &&
        (() => {
          const sourceGroup = userGroups.items[copyGroupIndex];
          const members = sourceGroup.members ?? [];
          return (
            <Modal
              closeIcon
              onClose={() => setCopyGroupIndex(undefined)}
              width={1400}
            >
              <div>
                <ManageGroupModalContent
                  onClose={() => setCopyGroupIndex(undefined)}
                  onSuccess={() =>
                    setToastMessage(t("users.groupCreatedMessage"))
                  }
                  groupCopy={{
                    name: `${sourceGroup.name}-kopia`,
                    description: sourceGroup.description,
                    permissions: sourceGroup.permissions,
                    skynetUserIds: copyGroupWithUsers
                      ? members.map((m) => m.skynetUserId)
                      : [],
                    groupAdminSkynetUserIds: copyGroupWithUsers
                      ? members
                          .filter((m) => m.isGroupAdmin)
                          .map((m) => m.skynetUserId)
                      : [],
                  }}
                  groupCopySkynetUsers={copyGroupWithUsers ? members : []}
                />
              </div>
            </Modal>
          );
        })()}
      {toastMessage && (
        <Toast
          type="success"
          title={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
      {deleteGroupSuccessful && (
        <Toast
          type="success"
          title={t("users.groupDeleted")}
          onClose={() => setDeleteGroupSuccessful(false)}
        />
      )}
    </div>
  );
};

export default GroupsList;
